import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DropdownList from 'react-widgets/DropdownList';
import FormGroup from '../../../FormGroup';
import Button from '../../../Button';
import ProfileAccountSettingsComponent from '../ProfileAccountSettings/ProfileAccountSettings.container';
import CustomModal from "../../../Modal";
import { I18nContext } from 'utils/I18n/context';

const images = {
    userIcon: require('img/icons/user.svg'),
    x: require('img/icons/x.svg'),
    trash: require('img/icons/icon-delete-red.svg')
};


export function ProfilePersonalInformation(props) {
    const {
        loading: pLoading,
        nickname: pNickname,
        advertiserId: pAdvertiserId,
        advertiserKey: pAdvertiserKey,
        name: pName,
        surname: pSurname,
        anonymous: pAnonymous,
        email: pEmail,
        subscribed: pSubscribed,
        rank: pRank,
        ranks: pRanks,
        contactTypes: pContactTypes,
        contacts: pContacts,
        settingsSave
    } = props;
    const { t } = useContext(I18nContext) || {};

    const [state, setState] = useState({
        loading: pLoading,
        nickname: pNickname,
        advertiserId: pAdvertiserId,
        advertiserKey: pAdvertiserKey,
        name: pName,
        surname: pSurname,
        anonymous: pAnonymous,
        email: pEmail,
        subscribed: pSubscribed,
        rank: pRank,
        ranks: pRanks,
        contactTypes: pContactTypes,
        contacts: pContacts,
        dataCurRank: {},
        errors: {},
        modalIsVisible: false,
        selectedContactType: null,
        addContactType: null,
        addContactValue: null,
        disabledConfirmButton: true
    });

    useEffect(() => {
        setState(prev => ({
            ...prev,
            loading: pLoading,
            nickname: pNickname,
            advertiserId: pAdvertiserId,
            advertiserKey: pAdvertiserKey,
            name: pName,
            surname: pSurname,
            anonymous: pAnonymous,
            email: pEmail,
            subscribed: pSubscribed,
            rank: pRank,
            ranks: pRanks,
            contactTypes: pContactTypes,
            contacts: pContacts
        }));
    }, [pLoading, pNickname, pAdvertiserId, pAdvertiserKey, pName, pSurname, pAnonymous, pEmail, pSubscribed, pRank, pRanks, pContactTypes, pContacts]);

    useEffect(() => {
        const data = (state.ranks || []).find((item) => item.rank === state.rank) || {};
        setState(prev => ({ ...prev, dataCurRank: data }));
    }, [state.rank, state.ranks]);

    const onChangeField = useCallback((event) => {
        const index = event.target.closest('.contacts')?.getAttribute('data-index');
        const value = event.target.value;
        setState(prev => ({
            ...prev,
            contacts: (prev.contacts || []).map((item, i) => ({
                ...item,
                value: i === Number(index) ? value : item.value
            }))
        }));
    }, []);

    const onChangeContactValue = useCallback((e, index) => {
        let contactType = e.target.getAttribute('data-type');
        let value = e.target.value;
        setState(prev => ({
            ...prev,
            contacts: (prev.contacts || []).map((item, i) => ({
                ...item,
                value: item.type === contactType && i === index ? value : item.value
            }))
        }));
    }, []);

    const onAddContactValue = useCallback((e) => {
        let value = e.target.value;
        setState(prev => ({
            ...prev,
            disabledConfirmButton: value.length <= 0 || prev.addContactType === null,
            addContactValue: value
        }));
    }, []);

    const onAddContactType = useCallback((type) => {
        setState(prev => ({
            ...prev,
            disabledConfirmButton: !prev.addContactValue,
            addContactType: type
        }));
    }, []);

    const onAddContact = useCallback(() => {
        setState(prev => {
            const contactType = prev.addContactType;
            const value = prev.addContactValue;
            const contacts = prev.contacts || [];
            return {
                ...prev,
                modalIsVisible: false,
                disabledConfirmButton: true,
                addContactType: null,
                addContactValue: null,
                contacts: contacts.map((item) => ({
                    ...item,
                    type: item.type === contactType.value || item.type === '' ? contactType.value : item.type,
                    value: item.type === contactType.value || item.type === '' ? value : item.value
                }))
            };
        });
    }, []);

    const onAddContactModal = useCallback(() => {
        setState(prev => ({
            ...prev,
            contacts: [...(prev.contacts || []), { type: '', value: '' }]
        }));
        showModal();
    }, []);

    const showModal = useCallback(() => {
        setState(prev => ({
            ...prev,
            addContactValue: null,
            addContactType: null,
            disabledConfirmButton: true,
            modalIsVisible: true
        }));
    }, []);

    const closeModal = useCallback(() => {
        setState(prev => {
            const contacts = prev.contacts || [];
            const selectedType = prev.selectedContactType;
            const items = contacts.filter((item) => {
                return (item.type !== '' && item.value !== '') || (selectedType && item.type !== selectedType.value);
            });
            return {
                ...prev,
                disabledConfirmButton: true,
                modalIsVisible: false,
                addContactValue: null,
                addContactType: null,
                contacts: items
            };
        });
    }, []);

    const onDeleteContact = useCallback((e) => {
        const index = Number(e.target.getAttribute('data-index'));
        setState(prev => ({
            ...prev,
            contacts: (prev.contacts || []).filter((item, i) => i !== index)
        }));
    }, []);

    const onChangeAnonymous = useCallback((event) => {
        const checked = !event.target.checked;
        setState(prev => ({ ...prev, anonymous: checked }));
    }, []);

    const onChangeSubscribed = useCallback((event) => {
        setState(prev => ({ ...prev, subscribed: event.target.checked }));
    }, []);

    const handleSettingsSave = useCallback((event) => {
        if (state.loading) return;
        event.preventDefault();
        setState(prev => ({ ...prev, loading: true }));
        // pass i18n t as second arg per actions signature
        settingsSave(state, t, () => {
            setState(prev => ({ ...prev, loading: false, errors: {}, modalIsVisible: false }));
        }, (errors) => {
            setState(prev => ({ ...prev, loading: false, errors }));
        });
    }, [state, settingsSave, t]);

    const isHaveErrorContact = useCallback((index, type) => {
        const contactsErrors = (state.errors && state.errors.contacts) || {};
        return !!contactsErrors[`[${index}][${type}]`];
    }, [state.errors]);

    const renderErrorContact = useCallback((index, type) => {
        const contactsErrors = (state.errors && state.errors.contacts) || {};
        if (contactsErrors[`[${index}][${type}]`]) {
            return <span className="error-message help-block">{contactsErrors[`[${index}][${type}]`]}</span>;
        }
        return null;
    }, [state.errors]);

    const staticNames = (t && t.profilePage && t.profilePage.personalInformation) || {};
    const staticNamesActions = (t && t.actions) || {};

    return (
        <form className="form-personal-info form" onSubmit={handleSettingsSave}>
            <div className="form-body">
                <div className="form-body__info">
                    <FormGroup
                        label={`${staticNames.name}:`}
                        errors={state.errors.name}
                        className={{"name-form": true}}
                    >
                        <input
                            id="name"
                            className="form-control"
                            type="text"
                            name="name"
                            value={state.name}
                            readOnly={true}
                            disabled={true}
                        />
                    </FormGroup>
                    <FormGroup
                        label={`${staticNames.surname}:`}
                        errors={state.errors.surname}
                        className={{"name-form": true}}
                    >
                        <input
                            id="surname"
                            className="form-control"
                            type="text"
                            name="surname"
                            value={state.surname}
                            readOnly={true}
                            disabled={true}
                        />
                    </FormGroup>
                    <FormGroup
                        label={`${staticNames.email}:`}
                        errors={state.errors.email}
                        className={{"name-form": true}}
                    >
                        <input
                            id="email"
                            className="form-control"
                            type="text"
                            name="email"
                            value={state.email}
                            readOnly={true}
                            disabled={true}
                        />
                    </FormGroup>
                </div>

                <div className="form-body__settings">
                    <FormGroup
                        label={`${staticNames.nickForTop}:`}
                        errors={state.errors.nickname}
                        className={{"name-form": true}}
                    >
                        <input
                            id="nickname"
                            className="form-control short-imput"
                            type="text"
                            name="nickname"
                            value={state.nickname}
                            onChange={onChangeField}
                            required
                            readOnly={true}
                            disabled={true}
                        />
                    </FormGroup>
                    <ProfileAccountSettingsComponent />
                    <FormGroup className={{ "news-form": true}}>
                        <label className="option" htmlFor="subscribed">
                            <input
                                id="subscribed"
                                className="option__input"
                                type="checkbox"
                                name="subscribed"
                                checked={state.subscribed}
                                onChange={onChangeSubscribed}
                            />
                            <span className="option__mark profile_mark" />
                            <span className="option__caption"> {staticNames.getNews}</span>
                        </label>
                    </FormGroup>
                </div>

                <div className="form-body__advertiser-info">
                    <p className="form-body__advertiser-info-header">{staticNames.advertiserInfo}</p>

                    <FormGroup
                        label={`${staticNames.advertiserId}:`}
                        errors={state.errors.advertiserId}
                        className={{"name-form": true}}
                    >
                        <input
                            id="advertiserId"
                            className="form-control short-imput"
                            type="text"
                            name="advertiserId"
                            value={state.advertiserId}
                            onChange={onChangeField}
                            required
                            readOnly={true}
                            disabled={true}
                        />
                    </FormGroup>

                    <FormGroup
                        label={`${staticNames.advertiserKey}:`}
                        errors={state.errors.advertiserKey}
                        className={{"name-form": true}}
                    >
                        <input
                            id="advertiserKey"
                            className="form-control short-imput"
                            type="text"
                            name="advertiserKey"
                            value={state.advertiserKey}
                            onChange={onChangeField}
                            required
                            readOnly={true}
                            disabled={true}
                        />
                    </FormGroup>
                </div>

                <div className="form-body__contacts">
                    <p className="form-body__contacts-header">{staticNames.contactsTitle || 'Contacts'}</p>
                    <CustomModal
                        visible={state.modalIsVisible}
                        onClose={closeModal}
                        onConfirm={onAddContact}
                        loading={state.loading}
                        title={staticNames.addContact || 'Add Contact'}
                        confirmDisabled={state.disabledConfirmButton}
                    >
                        <div className="form-horizontal">
                            <FormGroup>
                                <div>
                                    <div className={classNames({
                                        'has-error': isHaveErrorContact(1, 'type')
                                    })}>
                                        <DropdownList
                                            className={'rw-dropdownlist-select contact-type'}
                                            dataKey="value"
                                            textField="title"
                                            placeholder={staticNames.selectType || 'Select Type'}
                                            data={state.contactTypes}
                                            onChange={(value) => onAddContactType(value)}
                                        />
                                        {renderErrorContact(1, 'type')}
                                        <div className={classNames({
                                            'contact-number': true,
                                            'has-error': isHaveErrorContact(1, 'value')
                                        })}>
                                            <label htmlFor="addContactValue" className="sr-only">
                                                {staticNames.contactValue || 'Contact Value'}
                                            </label>
                                            <input
                                                id="addContactValue"
                                                className="form-control short-input"
                                                type="text"
                                                name="contactValue"
                                                onChange={onAddContactValue}
                                                data-type={state.selectedContactType}
                                                required={true}
                                                placeholder={staticNames.contactValue || 'Contact Value'}
                                            />
                                            {renderErrorContact(1, 'value')}
                                        </div>

                                    </div>
                                </div>
                            </FormGroup>
                        </div>
                    </CustomModal>

                    {state.contacts && state.contacts.map((item, index) => (
                        <div key={index} className="contacts" data-index={index}>
                            <div className={classNames({'has-error': isHaveErrorContact(index, 'type')})}>
                                <span className="contact__title">{item.type}</span>
                                {renderErrorContact(index, 'type')}
                                <div className={classNames({'contact-number': true, 'has-error': isHaveErrorContact(index, 'value')})}>
                                    <label htmlFor={`contactValue-${index}`} className="sr-only">
                                        {item.type} {staticNames.contactValue || 'Contact Value'}
                                    </label>
                                    <input
                                        id={`contactValue-${index}`}
                                        className="form-control short-input"
                                        type="text"
                                        name="contactValue"
                                        data-type={item.type}
                                        value={item.value}
                                        onChange={(value) => onChangeContactValue(value, index)}
                                        aria-label={`${item.type} contact value`}
                                    />
                                    <span
                                        className="contact-number__delete"
                                        onClick={onDeleteContact}
                                        data-index={index}
                                        aria-label="Delete contact"
                                        role="button"
                                        tabIndex={0}
                                    >
                                    </span>
                                    {renderErrorContact(index, 'value')}
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="form-body__contacts-add">
                        <Button
                            onClick={onAddContactModal}
                            extraClass={{addButton: true}}
                            text={staticNames.addContact || 'Add Contact'}
                            disabled={state.loading}
                        />
                    </div>
                </div>
            </div>
            <div className="form-footer">
                <FormGroup fieldClass={'save-form'}>
                    <Button
                        extraClass={{"btn apply-btn": true}}
                        loading={state.loading}
                        text={staticNamesActions.save || 'Save'}
                        type="submit"
                        disabled={state.loading}
                    />
                </FormGroup>
            </div>
        </form>
    );
}

ProfilePersonalInformation.propTypes = {
    loading: PropTypes.bool,
    nickname: PropTypes.string,
    advertiserId: PropTypes.string,
    advertiserKey: PropTypes.string,
    anonymous: PropTypes.bool,
    subscribed: PropTypes.bool,
    rank: PropTypes.number,
    ranks: PropTypes.array,
    contactTypes: PropTypes.array,
    contactType:  PropTypes.object,
    contactValue:  PropTypes.string,
};

ProfilePersonalInformation.defaultProps = {
    loading: false,
    nickname: '',
    advertiserId: '',
    advertiserKey: '',
    anonymous: true,
    subscribed: true,
    rank: 0,
    ranks: [],
    contactTypes: [],
    contactType: {},
    contactValue: '',
};
